/**
 * Loads a report filter's member tree by its (dimension, hierarchy) pair. Most pairs the deck's
 * filters use have no registered `hierarchy_id`, and without a tree the chip degrades to a flat
 * multiselect with no member-subset editor.
 */

import { getHierarchyTreeByPair } from 'src/components/star-grid/services/starGridService';
import { rollupsOnlyMembers } from 'src/components/star-grid/utils/rollupsOnlyMembers';
import type { PnlHierarchyTree } from 'src/components/star-grid/starGridTypes';

/** Separates dimension from hierarchy in the id report filters carry; the backend's own pair id form. */
export const HIERARCHY_PAIR_SEPARATOR = '::';

/** The id a report filter parameter carries, which is also what {@link loadFilterHierarchy} decodes. */
export const hierarchyPairId = (dimension: string, hierarchy: string): string => `${dimension}${HIERARCHY_PAIR_SEPARATOR}${hierarchy}`;

/**
 * Splits a pair id. Only the FIRST separator is significant — a hierarchy name may contain one, and
 * the dimension never does, so splitting greedily would corrupt the hierarchy.
 */
export const splitHierarchyPairId = (id: string): { dimension: string; hierarchy: string } | undefined => {
  const at = id.indexOf(HIERARCHY_PAIR_SEPARATOR);
  if (at <= 0) return undefined;
  const dimension = id.slice(0, at);
  const hierarchy = id.slice(at + HIERARCHY_PAIR_SEPARATOR.length);
  return dimension && hierarchy ? { dimension, hierarchy } : undefined;
};

export const loadFilterHierarchy = async (id: string, signal: AbortSignal): Promise<PnlHierarchyTree | undefined> => {
  const pair = splitHierarchyPairId(id);
  if (!pair) return undefined;
  const fetched = await getHierarchyTreeByPair(pair.dimension, pair.hierarchy, signal);
  // Report filters only: the Studio's pickers share this route and still need the leaves.
  const tree = fetched.rollups_only ? { ...fetched, members: rollupsOnlyMembers(fetched.members) } : fetched;
  // Echo the id we were ASKED for. `useReportParameter` rejects a tree whose `hierarchy_id` differs
  // from the parameter's, and the pair route answers with the REGISTRY id when the pair happens to be
  // registered — a legitimate tree for exactly the pair requested, which that guard would otherwise
  // throw away. The guard still does its job: it is the (dimension, hierarchy) we asked for.
  return { ...tree, hierarchy_id: id };
};
